import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  UilHeart, UilShieldCheck, UilStar, UilSmile,
  UilCheckCircle, UilMapMarker, UilPhone, UilCalendarAlt, UilClock, UilAward
} from '@iconscout/react-unicons';
import A_Clean from '../assets/Images/a-clean-house.jpg';
import Team from '../assets/Images/team.jpg';
import FooterPage from '../components/FooterPage';

const SERVICE_AREAS = [
  'Bellevue','Redmond','Sammamish','Woodinville','Kirkland','Seattle',
  'Mercer Island','Issaquah','Bothell','Magnolia','Lynnwood','Bainbridge Island',
  'Kenmore','Mountlake Terrace','Northgate','Lake Stevens','Edmonds',
];

const VALUES = [
  { icon: <UilHeart size={28} />,       title: 'Family Values',            description: 'Family-owned and operated since 1998, we treat every client like family', color: 'bg-rose-50 text-rose-600' },
  { icon: <UilShieldCheck size={28} />, title: 'Trust & Reliability',      description: 'Licensed, insured, and bonded for your complete peace of mind',           color: 'bg-blue-50 text-blue-600' },
  { icon: <UilStar size={28} />,        title: 'Excellence',               description: '5-star rated service with attention to every detail',                     color: 'bg-amber-50 text-amber-600' },
  { icon: <UilSmile size={28} />,       title: 'Satisfaction Guaranteed',  description: '100% happiness guarantee or we return to make it right',                  color: 'bg-emerald-50 text-emerald-600' },
];

const MILESTONES = [
  { year: '1998', title: 'Founded',        description: 'Started as a small family business with big dreams' },
  { year: '2005', title: 'Team Expansion', description: 'Grew our team to serve more clients across Seattle' },
  { year: '2015', title: '5-Star Rated',   description: 'Achieved consistent 5-star ratings on Google & Yelp' },
  { year: '2025', title: '25+ Years Strong', description: 'Serving thousands of happy customers with pride' },
];

const GUARANTEES = [
  { icon: <UilCheckCircle size={20} />, text: 'Professional & trained staff' },
  { icon: <UilStar size={20} />,        text: 'We recommend the best cleaning products' },
  { icon: <UilClock size={20} />,       text: 'Flexible scheduling options' },
  { icon: <UilAward size={20} />,       text: 'Quality workmanship guaranteed' },
];

export default function About() {
  return (
    <div className="bg-white text-slate-900">
      <Helmet>
        <title>About Us - Fabiana Fabulous Cleaning Services | Family-Owned Since 1998</title>
        <meta name="description" content="Family-owned cleaning service serving Seattle & Eastside since 1998. Professional, detailed, and reliable cleaning for residential and commercial clients." />
        <link rel="canonical" href="https://fabycleaners.com/about" />
      </Helmet>

      {/* Hero */}
      <section className="relative min-h-[65vh] flex items-end bg-cover bg-center" style={{ backgroundImage: `url(${A_Clean})` }}>
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-slate-900/20" />
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 py-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <span className="inline-block bg-gold-500/20 border border-gold-400/30 text-gold-300 text-xs font-semibold px-4 py-2 rounded-full mb-5">Trusted Since 1998</span>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-4 leading-tight">About Our Story</h1>
            <p className="text-white/65 text-lg max-w-xl">Family-owned since 1998, delivering top-tier cleaning across Eastside & Seattle.</p>
            <div className="flex flex-wrap gap-10 mt-10">
              {[['25+', 'Years Experience'], ['5.0', 'Star Rating'], ['5000+', 'Happy Customers']].map(([v, l]) => (
                <div key={l}>
                  <p className="text-4xl font-bold text-gold-400">{v}</p>
                  <p className="text-sm text-white/50 mt-1">{l}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Who We Are */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
              <div className="relative">
                <img src={Team} alt="Fabiana Fabulous Cleaning Services team" className="w-full h-[420px] object-cover rounded-3xl" />
                <div className="absolute -bottom-4 -right-4 w-full h-full border-2 border-brand-200 rounded-3xl -z-10" />
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
              <span className="inline-block bg-brand-50 text-brand-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-5">Who We Are</span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">A Family Business Built on Trust</h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>We're a family-owned cleaning service serving residential and commercial clients across Eastside & Seattle since 1998. From home and deep cleans to offices, move-in/out, Airbnb turnovers, and more — our team uses the best tools and treats every job as #1 priority.</p>
                <p>What started as a small family business has grown into a trusted name in the community, but we've never lost sight of our core values: integrity, attention to detail, and treating every client like family.</p>
                <p className="text-brand-600 font-semibold text-lg">Call today to see how we can make your space sparkle!</p>
              </div>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {GUARANTEES.map((item) => (
                  <div key={item.text} className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3">
                    <div className="text-brand-600 shrink-0">{item.icon}</div>
                    <span className="text-sm font-medium text-slate-700">{item.text}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
            <span className="inline-block bg-brand-50 text-brand-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-4">Our Core Values</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">The Principles That Guide Us</h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map((v, idx) => (
              <motion.div key={v.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.1 }}
                whileHover={{ y: -6 }} className="bg-white rounded-2xl p-7 border border-slate-100 shadow-card hover:shadow-card-hover transition-all text-center">
                <div className={`w-14 h-14 ${v.color} rounded-2xl flex items-center justify-center mx-auto mb-5`}>{v.icon}</div>
                <h3 className="font-bold text-slate-900 mb-2">{v.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{v.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
            <span className="inline-block bg-brand-50 text-brand-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-4">Our Journey</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">Milestones That Shaped Our Story</h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {MILESTONES.map((m, idx) => (
              <motion.div key={m.year} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.12 }}
                className="relative bg-slate-900 rounded-2xl p-7 overflow-hidden">
                <div className="absolute top-0 right-0 text-slate-700 text-7xl font-black select-none leading-none -mt-2 -mr-2">{m.year.slice(-2)}</div>
                <p className="text-gold-400 text-2xl font-bold mb-2">{m.year}</p>
                <h3 className="font-bold text-white mb-2">{m.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{m.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-20 bg-brand-600">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-white/10 text-white text-sm font-semibold px-4 py-2 rounded-full mb-5">
              <UilMapMarker size={16} /> Proudly Serving
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">Seattle & Eastside Communities</h2>
            <p className="text-brand-100 max-w-xl mx-auto">We bring exceptional cleaning services to your neighborhood.</p>
          </motion.div>
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {SERVICE_AREAS.map((area, idx) => (
              <motion.span key={area} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: idx * 0.04 }}
                className="bg-white/10 hover:bg-white/20 text-white text-sm font-medium px-4 py-2 rounded-xl transition-colors cursor-default">
                {area}
              </motion.span>
            ))}
          </div>
          <div className="text-center">
            <p className="text-brand-100 mb-6">Ready to experience the Fabiana difference?</p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a href="tel:425-829-6092"
                className="inline-flex items-center justify-center gap-2 bg-white hover:bg-brand-50 text-brand-700 font-bold px-8 py-3.5 rounded-xl transition-colors">
                <UilPhone size={18} /> Call (425) 829-6092
              </a>
              <Link to="/request"
                className="inline-flex items-center justify-center gap-2 border-2 border-white/40 hover:border-white text-white font-semibold px-8 py-3.5 rounded-xl transition-all">
                <UilCalendarAlt size={18} /> Get Free Quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      <FooterPage />
    </div>
  );
}
